import type { ClipboardEvent, KeyboardEvent } from 'react';
import { InvoiceableDurationStep, MAX_LIMIT_VALUE_TRANSACTION } from '../constants/constants';
import { durationStepConverter, usesCommaDecimalSeparator } from '../constants/utilityFunctions';

export const DEFAULT_HOURS_STEP = 0.25;
export const PRICE_STEP = 1;

const CENTS_PER_UNIT = 100;
const WHITESPACE = /[\s  ]/g;
const SEPARATORS = /[.,]/g;
const ALLOWED_TEXT = /^\d{0,4}([.,]\d{0,2})?$/;
const DIGITS_IN_A_GROUP = 3;

export const getHoursStep = (durationStep?: InvoiceableDurationStep) => {
  if (!durationStep || durationStep === InvoiceableDurationStep.NONE) return DEFAULT_HOURS_STEP;
  return durationStepConverter(durationStep) / 60;
};

export const parseRegionalNumber = (text: string) => {
  const compact = text.replaceAll(WHITESPACE, '');
  if (!compact) return 0;

  const lastSeparatorIndex = Math.max(compact.lastIndexOf('.'), compact.lastIndexOf(','));
  if (lastSeparatorIndex === -1) return Number(compact);

  const separator = compact[lastSeparatorIndex];
  const localeDecimal = usesCommaDecimalSeparator() ? ',' : '.';
  const fraction = compact.slice(lastSeparatorIndex + 1);
  const integer = compact.slice(0, lastSeparatorIndex);
  if (integer.includes(separator)) return Number.NaN;

  const isGroupSeparator = fraction.length === DIGITS_IN_A_GROUP && separator !== localeDecimal;
  if (isGroupSeparator) return Number(compact.replaceAll(SEPARATORS, ''));
  return Number(`${integer.replaceAll(SEPARATORS, '')}.${fraction}`);
};

export const stepNumericValue = ({
  text,
  step,
  direction,
}: {
  text: string;
  step: number;
  direction: 'up' | 'down';
}) => {
  const current = parseRegionalNumber(text);
  const currentCents = Number.isNaN(current) ? 0 : Math.round(current * CENTS_PER_UNIT);
  const stepCents = Math.round(step * CENTS_PER_UNIT);
  const maxCents = Math.round(MAX_LIMIT_VALUE_TRANSACTION * CENTS_PER_UNIT);
  const nextCents = currentCents + (direction === 'up' ? stepCents : -stepCents);
  return (Math.min(Math.max(nextCents, 0), maxCents) / CENTS_PER_UNIT).toFixed(2);
};

export const isAllowedNumericText = (text: string) => ALLOWED_TEXT.test(text);

const textAfterReplacingSelection = (input: HTMLInputElement, inserted: string) => {
  const { value } = input;
  const start = input.selectionStart ?? value.length;
  const end = input.selectionEnd ?? value.length;
  return value.slice(0, start) + inserted + value.slice(end);
};

export const handleNumericFieldKeyDown = (
  event: KeyboardEvent<HTMLInputElement>,
  { step, onStep }: { step: number; onStep: (value: string) => void },
) => {
  const isShortcut = event.ctrlKey || event.metaKey || event.altKey;
  if (isShortcut) return;

  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    event.preventDefault();
    const direction = event.key === 'ArrowUp' ? 'up' : 'down';
    onStep(stepNumericValue({ text: event.currentTarget.value, step, direction }));
    return;
  }

  const isNamedKey = event.key.length > 1;
  if (isNamedKey) return;

  if (!isAllowedNumericText(textAfterReplacingSelection(event.currentTarget, event.key))) {
    event.preventDefault();
  }
};

export const handleNumericFieldPaste = (event: ClipboardEvent<HTMLInputElement>) => {
  const pasted = event.clipboardData.getData('text').trim();
  if (!isAllowedNumericText(textAfterReplacingSelection(event.currentTarget, pasted))) {
    event.preventDefault();
  }
};
